import { CALL_TYPES, CONSENT_TYPES, LEAD_SOURCES, PRODUCT_TYPES, US_STATES } from "@tlcrm/shared";
import { useNavigate } from "react-router";

import { useCreateLead, usePicklists } from "../api/hooks";
import { useMe } from "../auth/session";
import { FieldBox, Modal, Select } from "../components/ui";
import { useToast } from "../components/Toasts";
import { useForm } from "./useForm";

/**
 * A lead typed in by hand: a referral, a walk-in, a call the phone system did not file. A number
 * already on file attaches the lead to that person (the API decides, by the number), so the same
 * caller asking twice is two leads on one contact.
 *
 * The lead vendor sees only a name, a number and the consent: the API files theirs as a vendor
 * lead, Call Type TV Lead, and hands back the open lead it already has for a number it knows.
 */
export function NewLeadModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { me } = useMe();
  const isVendor = me.user.role === "lead_vendor";
  const toast = useToast();
  const create = useCreateLead();
  const picklists = usePicklists();
  const f = useForm({
    firstName: "",
    lastName: "",
    phone: "",
    state: "",
    source: "referral" as (typeof LEAD_SOURCES)[number][0],
    vendorId: "",
    campaign: "",
    productInterest: "" as string,
    consentType: (isVendor ? "express_written" : "express") as (typeof CONSENT_TYPES)[number][0],
    callType: "" as string,
  });
  const v = f.values;

  const submit = async () => {
    try {
      const lead = await create.mutateAsync({
        ...(v.callType && { callType: v.callType }),
        contact: {
          firstName: v.firstName,
          lastName: v.lastName,
          phone: v.phone,
          state: v.state || null,
        },
        source: v.source,
        vendorId: v.source === "vendor" && v.vendorId ? v.vendorId : null,
        campaign: v.campaign,
        productInterest: v.productInterest || null,
        consentType: v.consentType,
        // Typed in now, from a person who just gave it: the moment is now.
        consentAt: v.consentType === "none" ? null : new Date().toISOString(),
      });
      if (isVendor) {
        // The vendor's answer is the lead's id and whether it was new; they cannot open it.
        const r = lead as unknown as { created: boolean };
        toast(
          r.created ? "Lead sent." : "That number already has an open lead; it was not sent twice.",
        );
        onClose();
        return;
      }
      toast(`Lead for ${lead.contactName} added.`);
      onClose();
      navigate(`/leads/${lead.id}`);
    } catch (err) {
      f.fail(err, {
        "contact.phone": "phone",
        "contact.lastName": "lastName",
        "contact.firstName": "firstName",
      });
    }
  };

  return (
    <Modal
      title="New lead"
      onClose={onClose}
      width={600}
      footer={
        <>
          {f.formError && (
            <span className="left" style={{ color: "var(--brick)" }}>
              {f.formError}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={create.isPending}
          >
            {create.isPending ? "Saving…" : "Create lead"}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FieldBox label="First name" htmlFor="nl-first" error={f.errors.firstName}>
          <input id="nl-first" {...f.text("firstName")} />
        </FieldBox>
        <FieldBox label="Last name" htmlFor="nl-last" error={f.errors.lastName}>
          <input id="nl-last" {...f.text("lastName")} />
        </FieldBox>
        <FieldBox label="Phone" htmlFor="nl-phone" error={f.errors.phone}>
          <input
            id="nl-phone"
            inputMode="tel"
            placeholder="(407) 555-0188"
            {...f.text("phone", "mono")}
          />
        </FieldBox>
        <FieldBox label="State" htmlFor="nl-state">
          <Select
            id="nl-state"
            value={v.state}
            onChange={f.set("state")}
            options={US_STATES}
            empty="Not known"
          />
        </FieldBox>
        {!isVendor && (
          <>
            <FieldBox label="Source" htmlFor="nl-source">
              <Select
                id="nl-source"
                value={v.source}
                onChange={(x) => f.set("source")(x || "referral")}
                options={LEAD_SOURCES}
              />
            </FieldBox>
            {v.source === "vendor" ? (
              <FieldBox label="Vendor" htmlFor="nl-vendor" error={f.errors.vendorId}>
                <Select
                  id="nl-vendor"
                  value={v.vendorId}
                  onChange={f.set("vendorId")}
                  options={(picklists.data?.leadVendors ?? [])
                    .filter((x) => x.active)
                    .map((x) => [x.id, x.name] as const)}
                  empty="Choose a vendor"
                />
              </FieldBox>
            ) : (
              <FieldBox label="Campaign" htmlFor="nl-campaign">
                <input id="nl-campaign" {...f.text("campaign")} />
              </FieldBox>
            )}
            <FieldBox label="Call Type" htmlFor="nl-calltype">
              <Select
                id="nl-calltype"
                value={v.callType}
                onChange={f.set("callType")}
                options={CALL_TYPES}
                empty="None"
              />
            </FieldBox>
            <FieldBox label="Interested in" htmlFor="nl-product">
              <Select
                id="nl-product"
                value={v.productInterest}
                onChange={f.set("productInterest")}
                options={PRODUCT_TYPES}
                empty="Not said"
              />
            </FieldBox>
          </>
        )}
        <FieldBox label="Consent" htmlFor="nl-consent" error={f.errors.consentAt}>
          <Select
            id="nl-consent"
            value={v.consentType}
            onChange={(x) => f.set("consentType")(x || "none")}
            options={CONSENT_TYPES}
          />
        </FieldBox>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
